import React, { useState } from 'react';
import { XIcon, PlusIcon, MapPinIcon } from 'lucide-react';
import { Province, StoreTier } from '../../data/types';
import { provinces, tiers } from '../../data/stores';
import { Button } from '../ui/Button';
import { Select } from '../ui/Select';
interface AddStoreModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddStore: (store: {
    name: string;
    location: string;
    province: Province;
    tier: StoreTier;
  }) => void;
}
export function AddStoreModal({
  isOpen,
  onClose,
  onAddStore
}: AddStoreModalProps) {
  const [name, setName] = useState('');
  const [location, setLocation] = useState('');
  const [province, setProvince] = useState<Province | ''>('');
  const [tier, setTier] = useState<StoreTier | ''>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  if (!isOpen) return null;
  const isValid = name.trim() && location.trim() && province && tier;
  const handleSubmit = async () => {
    if (!isValid || !province || !tier) return;
    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 400));
    onAddStore({
      name: name.trim(),
      location: location.trim(),
      province,
      tier
    });
    setIsSubmitting(false);
    setName('');
    setLocation('');
    setProvince('');
    setTier('');
    onClose();
  };
  return <>
      <div className="fixed inset-0 bg-black/40 z-50 transition-opacity" onClick={onClose} data-id="element-11" />

      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-12">
        <div className="bg-white w-full max-w-lg shadow-2xl pointer-events-auto flex flex-col rounded-xl" data-id="element-13">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-border" data-id="element-14">
            <div className="flex items-center gap-2.5" data-id="element-15">
              <div className="w-8 h-8 rounded-sm bg-primary/5 flex items-center justify-center" data-id="element-16">
                <MapPinIcon className="w-4 h-4 text-primary" data-id="element-17" />
              </div>
              <h2 className="text-lg font-semibold text-text-primary" data-id="element-18">
                Add New Store
              </h2>
            </div>
            <button onClick={onClose} className="p-1.5 text-text-tertiary hover:text-text-primary transition-colors rounded-sm hover:bg-surface" data-id="element-19">
              <XIcon className="w-5 h-5" data-id="element-20" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-5" data-id="element-21">
            {/* Store Name */}
            <div data-id="element-22">
              <label htmlFor="store-name" className="block text-sm font-medium text-text-primary mb-1.5" data-id="element-23">
                Store Name <span className="text-urgent" data-id="element-24">*</span>
              </label>
              <input id="store-name" type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Menlyn Park" className="w-full px-3 py-2 border border-border rounded-sm text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary" data-id="element-25" />
            </div>

            {/* Location */}
            <div data-id="element-26">
              <label htmlFor="store-location" className="block text-sm font-medium text-text-primary mb-1.5" data-id="element-27">
                Location <span className="text-urgent" data-id="element-28">*</span>
              </label>
              <input id="store-location" type="text" value={location} onChange={e => setLocation(e.target.value)} placeholder="e.g. Menlyn, Pretoria" className="w-full px-3 py-2 border border-border rounded-sm text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary" data-id="element-29" />
            </div>

            {/* Province & Tier row */}
            <div className="grid grid-cols-2 gap-4" data-id="element-30">
              <div data-id="element-31">
                <Select label="Province" placeholder="Select Province" value={province} onChange={e => setProvince(e.target.value as Province)} options={provinces.map(p => ({
                value: p,
                label: p
              }))} data-id="element-32" />
              </div>
              <div data-id="element-33">
                <Select label="Store Tier" placeholder="Select Tier" value={tier} onChange={e => setTier(e.target.value as StoreTier)} options={tiers.map(t => ({
                value: t,
                label: t.charAt(0).toUpperCase() + t.slice(1).replace('-', ' ')
              }))} data-id="element-34" />
              </div>
            </div>

            {/* Info note */}
            <div className="p-3 bg-surface-warm rounded-sm border border-border" data-id="element-35">
              <p className="text-xs text-text-secondary leading-relaxed" data-id="element-36">
                The new store will be created with 12 weeks of availability
                across all media types (Digital Outposts, Digital Drive Aisle,
                Digital Windows). All slots will start as available.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-border bg-surface flex items-center justify-between" data-id="element-37">
            <Button variant="ghost" onClick={onClose} data-id="element-38">
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSubmit} disabled={!isValid || isSubmitting} className="flex items-center gap-2" data-id="element-39">
              {isSubmitting ? 'Adding...' : <>
                  <PlusIcon className="w-4 h-4" data-id="element-40" />
                  Add Store
                </>}
            </Button>
          </div>
        </div>
      </div>
    </>;
}